// ErrorBoundary — the last line between a render error and a blank white page.
//
// React 18 unmounts the WHOLE tree when a render throws with no boundary above
// it, which is what the office sees as "the screen went white" — no message,
// no way back but a hard refresh, and nothing for anyone to report. This
// boundary keeps the shell on screen, names the error, logs the component
// stack, and offers a reload. It never swallows: the error still reaches
// console.error, so a bug report can carry it.

import { Component, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';

interface State { error: Error | null }

type Props = PropsWithChildren<{
  // A value that changes when the user navigates (the route pathname). A
  // caught error is cleared when it changes, so leaving the broken page shows
  // the next page instead of the stale error card. This is a PROP, not a React
  // `key` on the boundary: keying the boundary on the pathname remounted every
  // page on every URL change — the Text Inbox and Email Inbox keep the open
  // conversation in the URL, so each click tore the whole page down and
  // rebuilt it (every subscription and fetch again, a visible double paint).
  resetKey?: string;
}>;

export class ErrorBoundary extends Component<Props, State> {
  // The repo ships no React type definitions, so the base class is untyped
  // and `this.props` / `this.state` must be declared here to type-check.
  declare props: Readonly<Props>;
  declare state: Readonly<State>;
  declare setState: (state: Partial<State>) => void;

  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex-1 min-h-0 flex items-center justify-center p-6" role="alert">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-2xl p-5 shadow-sm">
          <p className="text-base font-bold text-slate-900">Something went wrong on this page</p>
          <p className="text-xs text-slate-500 mt-1">
            The rest of the app is fine. Reload to continue; if it happens again, send this line to support:
          </p>
          <pre className="mt-3 text-[11px] text-red-700 bg-red-50 rounded-lg p-2 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold px-4 py-2 rounded-xl"
          >
            <RefreshCw size={14} /> Reload
          </button>
        </div>
      </div>
    );
  }
}
